import React, { useState } from 'react';
import { CalendarCheck, Image as ImageIcon, LayoutDashboard, LogOut, Monitor, Package, Shield, ShieldCheck, ShoppingBag, Truck, Users, Menu, X } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ROLE_LABELS } from '../config';
import { useAuth } from '../context/AuthContext';
import NotificationsBell from './admin/NotificationsBell';

const navItems = [
  { to: '/my/dashboard', icon: LayoutDashboard, label: 'Tableau de bord', permission: 'dashboard:view' },
  { to: '/my/produits', icon: Package, label: 'Produits', permission: 'products:view' },
  { to: '/my/photo-audit', icon: ImageIcon, label: 'Audit photos', permission: 'products:manage' },
  { to: '/my/reservations', icon: CalendarCheck, label: 'Réservations', permission: 'reservations:view' },
  { to: '/my/commandes', icon: ShoppingBag, label: 'Commandes', permission: 'orders:view' },
  { to: '/my/fournisseurs', icon: Truck, label: 'Fournisseurs', permission: 'products:manage' },
  { to: '/my/utilisateurs', icon: Users, label: 'Utilisateurs', permission: 'users:view' },
  // Réglage du barème de la Garantie Atelier — même permission de lecture que les
  // autres réglages du back-office (cf. `maintenance:view` sur le dashboard).
  { to: '/my/garantie', icon: ShieldCheck, label: 'Garantie Atelier', permission: 'maintenance:view' },
];

export default function AdminLayout({ children, title }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, hasPermission, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const items = navItems.filter((item) => hasPermission(item.permission));

  const handleLogout = async () => {
    await logout();
    navigate('/my', { replace: true });
  };

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="admin-layout">
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-primary text-white border-r border-white/5 transform transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}
      >
        <div className="p-5 border-b border-white/10">
          <Link to="/my/dashboard" className="flex items-center gap-3" onClick={() => setSidebarOpen(false)}>
            <img
              src="/logo-meteris.png"
              alt="Météris"
              className="h-10 w-10 rounded-full object-cover ring-1 ring-secondary/30"
            />
            <div className="leading-none">
              <span className="block font-heading font-bold text-sm uppercase tracking-tight">Administration</span>
              <span className="block font-accent text-[10px] uppercase tracking-[0.2em] text-secondary mt-0.5">Back-office boutique</span>
            </div>
          </Link>
        </div>

        {user && (
          <div className="px-5 py-4 border-b border-white/10">
            <div className="flex items-center gap-3">
              {user.picture ? (
                <img src={user.picture} alt="" className="w-9 h-9 rounded-full object-cover ring-1 ring-white/10" />
              ) : (
                <div className="w-9 h-9 rounded-sm bg-secondary/15 border border-secondary/30 flex items-center justify-center text-secondary text-sm font-heading font-bold">
                  {(user.name || user.email || '?')[0].toUpperCase()}
                </div>
              )}
              <div className="min-w-0">
                <p className="text-sm font-semibold text-white truncate">{user.name || 'Admin'}</p>
                <p className="text-[10px] font-accent uppercase tracking-widest text-slate-400 truncate">{ROLE_LABELS[user.role] || user.role}</p>
              </div>
            </div>
          </div>
        )}

        <nav className="p-3 space-y-1">
          {items.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-sm font-accent text-sm uppercase tracking-wider transition-colors ${
                location.pathname === item.to || location.pathname.startsWith(`${item.to}/`)
                  ? 'bg-secondary/15 text-secondary border-l-2 border-secondary'
                  : 'text-slate-300 hover:text-white hover:bg-white/5 border-l-2 border-transparent'
              }`}
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-white/10 space-y-1">
          <Link
            to="/compte"
            className="flex items-center gap-3 px-3 py-2 rounded-sm font-accent text-sm uppercase tracking-wider text-slate-300 hover:text-white hover:bg-white/5 transition-colors"
          >
            <Shield className="w-4 h-4" />
            Espace client
          </Link>
          <Link
            to="/"
            className="flex items-center gap-3 px-3 py-2 rounded-sm font-accent text-sm uppercase tracking-wider text-slate-300 hover:text-white hover:bg-white/5 transition-colors"
          >
            <Monitor className="w-4 h-4" />
            Voir le site
          </Link>
          <button
            type="button"
            onClick={handleLogout}
            className="flex items-center gap-3 px-3 py-2 rounded-sm font-accent text-sm uppercase tracking-wider text-rose-400 hover:bg-rose-500/10 transition-colors w-full text-left"
            data-testid="admin-logout"
          >
            <LogOut className="w-4 h-4" />
            Déconnexion
          </button>
        </div>
      </aside>

      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <div className="flex-1 lg:ml-64 min-w-0">
        <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-slate-200 px-4 lg:px-8 py-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden p-2 rounded-sm border border-slate-200 hover:bg-slate-100 transition-colors"
                data-testid="admin-sidebar-toggle"
                aria-label={sidebarOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
              >
                {sidebarOpen ? <X className="w-5 h-5 text-slate-700" /> : <Menu className="w-5 h-5 text-slate-700" />}
              </button>
              <h1 className="font-heading text-lg md:text-xl uppercase tracking-tight text-primary">{title}</h1>
            </div>
            <div className="flex items-center gap-3">
              <span className="font-accent text-[10px] uppercase tracking-widest text-slate-500 hidden sm:inline">{user?.email}</span>
              {/* Dans l'en-tête, donc sur TOUTES les pages d'administration : une
                  alerte naît d'un geste fait ailleurs (un paiement encaissé pendant
                  qu'on est sur les réservations). Cf. la docstring du composant. */}
              <NotificationsBell />
            </div>
          </div>
        </header>

        <main className="p-4 lg:p-8">{children}</main>
      </div>
    </div>
  );
}
